import type { PitchOption, PitchOptionSolution } from "@/lib/pitchAssignmentOptions";

const RULE_PROBLEM_KINDS = new Set([
  "electricity",
  "vehicle_length",
  "vehicle_width",
  "vehicle_height",
  "vehicle_weight",
  "arrival_type",
  "usage_type",
]);

export function solutionConsequence(solution: PitchOptionSolution): string | null {
  switch (solution.kind) {
    case "reassign_booking":
      return (
        "Die betroffene Buchung verliert ihren Stellplatzvorschlag und erhält nach der Zuweisung " +
        "automatisch einen neuen Vorschlag. Es entsteht keine Doppelbelegung."
      );
    case "ignore_category":
      return (
        "Die Platzkategorie der Buchung wird an den gewählten Stellplatz angepasst. " +
        "Tarif, Verfügbarkeit und spätere Auto-Zuweisungen können sich ändern."
      );
    case "ignore_electricity":
      return "Der Stromwunsch wird ignoriert – der Gast erhält keinen Stromanschluss auf diesem Platz.";
    case "ignore_vehicle_length":
      return "Die maximale Fahrzeuglänge des Platzes wird ignoriert – der Platz kann zu klein sein.";
    case "ignore_vehicle_width":
      return "Die Breitenbeschränkung des Platzes wird ignoriert.";
    case "ignore_vehicle_height":
      return "Die Höhenbeschränkung des Platzes wird ignoriert.";
    case "ignore_vehicle_weight":
      return "Die Gewichtsbeschränkung des Platzes wird ignoriert.";
    case "ignore_arrival_type":
      return "Die Anreiseart-Einschränkung des Platzes wird ignoriert.";
    case "unfix_booking":
      return (
        "Die fixierte Buchung muss manuell aufgehoben und umgeplant werden, " +
        "bevor dieser Platz zugewiesen werden kann."
      );
    case "remove_block_period":
      return "Der Sperrzeitraum muss in den Stammdaten angepasst oder entfernt werden.";
    case "change_category":
      return "Passen Sie die Buchungskategorie an oder wählen Sie einen anderen Stellplatz.";
    default:
      return null;
  }
}

/** Resolve all automated flags needed so the assignment succeeds without double booking. */
export function buildAssignOptionsFromSolution(
  option: PitchOption,
  _solution: PitchOptionSolution,
): {
  reassign_conflicts?: boolean;
  ignore_rules?: boolean;
  ignore_category?: boolean;
} {
  const problemKinds = new Set(option.problems.map((problem) => problem.kind));
  const opts: {
    reassign_conflicts?: boolean;
    ignore_rules?: boolean;
    ignore_category?: boolean;
  } = {};

  if (problemKinds.has("occupied_suggestion")) {
    opts.reassign_conflicts = true;
  }
  if (problemKinds.has("category_mismatch")) {
    opts.ignore_category = true;
  }
  if ([...problemKinds].some((kind) => RULE_PROBLEM_KINDS.has(kind))) {
    opts.ignore_rules = true;
  }

  return opts;
}

export const APPLICABLE_SOLUTION_KINDS = [
  "reassign_booking",
  "ignore_category",
  "ignore_vehicle_length",
  "ignore_vehicle_width",
  "ignore_vehicle_height",
  "ignore_vehicle_weight",
  "ignore_electricity",
  "ignore_arrival_type",
] as const;
